import { Button, CircularProgress, Divider, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { useAtom } from "jotai";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import InputMask from 'react-input-mask';
import { permissionsAtom, userTypeAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EmployeeList from "../../components/employees/EmployeeList";
import PrivacyPolicyLink from "../../components/privacy-policy-link/PrivacyPolicyLink";
import { EmployeeService } from "../../services/EmployeeService";
import { LoginService } from "../../services/LoginService";
import { AccountDTO } from "../../types/AccountDTO";
import { getCategories } from "../../types/CategoryDTO";
import { EmployeeDTO } from "../../types/EmployeeDTO";
import { PositionDTO } from "../../types/PositionDTO";
import { SectorDTO } from "../../types/SectorDTO";
import { useUtils } from "../../utils/utilFunctions";
import "./Employees.scss";

const Employees: FC = () => {
	const isMounted = useRef(true);
	const [cpf, setCpf] = useState('');
	const [cnpj, setCnpj] = useState('');
	const [name, setName] = useState('');
	const [rg, setRg] = useState('');
	const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
	const [admissionDate, setAdmissionDate] = useState<Dayjs | null>(null);
	const [sex, setSex] = useState('');
	const [sector, setSector] = useState('');
	const [position, setPosition] = useState('');
	const [newPosition, setNewPosition] = useState('');
	const [functionName, setFunction] = useState('');
	const [pis, setPis] = useState('');
	const [ctps, setCtps] = useState('');
	const [esocial, setEsocial] = useState('');
	const [category, setCategory] = useState('');
	const [user, setUser] = useState<AccountDTO>({} as AccountDTO);
	const [userType] = useAtom(userTypeAtom);
	const [loadingCpfValidation, setLoadingCpfValidation] = useState(false);
	const [loadingCreate, setLoadingCreate] = useState(false);
	const [firstClick, setFirstClick] = useState(false);
	const [sectors, setSectors] = useState<Map<string, SectorDTO[]>>(new Map());
	const [positions, setPositions] = useState<Map<string, PositionDTO[]>>(new Map());
	const [permissions] = useAtom(permissionsAtom);
	const [updates, setUpdates] = useState(0);
	const { verifyPermission, validateCPF, formatDocument } = useUtils();
	const { showError, showAlert } = useAlert();
	const service = new EmployeeService();
	const notAllowedCreate = !cpf || !name || !sex || !birthDate || !admissionDate
		|| !position || (position === '-1' && !newPosition) || (pis !== '' && pis.includes("_"));
	const externalUser = "EXTERNO" === userType;
	const sectorName = sectors.get(cnpj)?.find(i => i.id_setor === sector)?.setor;
	const positionName = positions.get(sector)?.find(i => i.id_cargo === position)?.cargo;

	const invalidCpf = !!cpf && !cpf.includes("_") && !validateCPF(cpf);

	const cleanForm = () => {
		setCpf('');
		setName('');
		setBirthDate(null);
		setRg('');
		setSex('');
		setSector('');
		setPosition('')
		setFunction('');
		setAdmissionDate(null);
		setPis('');
		setCtps('');
		setEsocial('');
		setCategory('');
		setFirstClick(false);
	}

	const handleChangePosition = (value: string) => {
		setPosition(value);
		setFunction(positions.get(sector)?.find(i => i.id_cargo === value)?.cargo || '');
		setNewPosition('');
	}

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!isMounted.current) return;
		if (!!permissions.length) {
			verifyPermission(permissions, 'EMPLOYEES');
		}
	}, []);

	useEffect(() => {
		const loadCnpjsFromExternalUser = async () => {
			if (!externalUser) return;
			const response = await new LoginService().getDataUser();
			if (response && response.data) {
				setUser(response.data);
			}
		};
		loadCnpjsFromExternalUser();
	}, []);

	useEffect(() => {
		if (!cnpj || cnpj.includes('_') || !!sectors.get(cnpj)) return;
		const loadSectors = async () => {
			const response = await service.getSectorsByCnpj(cnpj);
			if (response && response.data) {
				const newMap = new Map(sectors);
				newMap.set(cnpj, response.data);
				setSectors(newMap);
			}
		};
		loadSectors();
	}, [cnpj]);

	useEffect(() => {
		if (!sector || !!positions.get(sector)) return;
		const loadPositions = async () => {
			const response = await service.getPositionsBySector(sector);
			if (response && response.data) {
				const newMap = new Map(positions);
				newMap.set(sector, response.data);
				setPositions(newMap);
			}
		};
		loadPositions();
	}, [sector]);

	return (
		<div>
			<AppBar
				modulo="Funcionários"
			/>
			<div className="employees">
				<div className="form-side">
					<div className="form-title"><Typography sx={{fontSize: "3vh"}}>Cadastrar funcionário</Typography></div>
					<div className="form-card">
						<div className="form-line">
							{externalUser && user?.permissionType === "CNPJ"
								? <FormControl fullWidth size="small">
									<InputLabel>
										CNPJ
									</InputLabel>
									<Select
										label='CNPJ'
										id="cnpj-form"
										value={cnpj}
										onChange={(e) => setCnpj(e.target.value)}
										size='small'
										disabled={!!name}
									>
										{(user?.permissionCnpjs || []).map((item) => (
											<MenuItem
											key={item}
											value={item}
										>
											{item}
										</MenuItem>
										))}
									</Select>
								</FormControl>
								: <TextField
									label="CPF/CNPJ da empresa"
									size="small"
									disabled={!!name}
									inputProps={{ maxLength: 18}}
									fullWidth
									value={cnpj}
									onChange={(e: ChangeEvent<HTMLInputElement>) => setCnpj(formatDocument(e.target.value.trim()))}
								/>
							}
							<InputMask mask="999.999.999-99"
								value={cpf}
								disabled={!!name}
								onChange={(e: ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
								>
								{(inputProps: any) => (
									<TextField
										error={invalidCpf}
										helperText={invalidCpf ? "CPF inválido" : ""}
										size="small"
										{...inputProps}
										label={"Digite o CPF do funcionário"}
										variant="outlined"
										disabled={!!name}
										fullWidth
									/>
								)}
							</InputMask>
							{cpf && !cpf.includes("_") && !!cnpj && !name && !invalidCpf
								? <Button
									sx={{width: "20vh", height: "100%"}}
									variant="contained"
									color="primary"
									onClick={() => validateCpf()}
									id="validation-button"
								>
									{loadingCpfValidation
										? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
										: "Validar"
									}
								</Button>
								: <></>
							}
						</div>
						{cpf && name && birthDate
							? <>
								<TextField
									size="small"
									fullWidth
									label="Nome do funcionário"
									value={name}
									error={firstClick && !name}
								/>
								<FormControl className="sex-input">
									<FormLabel
										id="employee-sex"
										sx={{display: "flex", alignItems: "center"}}
										error={firstClick && !sex}
									>
										Sexo:
									</FormLabel>
									<RadioGroup
										id="sex-radio"
										row
										value={sex}
										color="primary"
									>
										<FormControlLabel sx={{ color: 'text.primary' }} value="M" control={<Radio />} label="Masculino" />
										<FormControlLabel sx={{ color: 'text.primary' }} value="F" control={<Radio />} label="Feminino" />
									</RadioGroup>
								</FormControl>
								<div className="form-line">
									<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
										<DemoContainer sx={{width: "100%"}} components={['DatePicker']}>
											<DatePicker
												className='bith-date'
												label="Data de nascimento"
												value={birthDate}
												sx={{ minWidth: '150px !important', width: { xs: "100%", sm: "28vh"} }}
												readOnly
												slotProps={{
													textField: {
														error: firstClick && !birthDate,
													},
												}}
											/>
											<DatePicker
												className='admission-date'
												label="Data de admissão"
												value={admissionDate}
												sx={{ marginBottom: '7px !important', minWidth: '150px !important', width: { xs: "100%", sm: "28vh"} }}
												onChange={(newValue) => setAdmissionDate(newValue)}
												slotProps={{
													textField: {
														error: firstClick && !admissionDate,
													},
												}}
											/>
										</DemoContainer>
									</LocalizationProvider>
								</div>
								<div className="form-line">
									<FormControl sx={{width: { xs: "100%", sm: "28vh"}}}>
										<InputLabel>
											Setor
										</InputLabel>
										<Select
											label='Setor'
											id="select-form"
											value={sector}
											onChange={(e) => {
												setSector(e.target.value);
												setPosition('');
											}}
											size='medium'
											error={firstClick && !sector}
										>
											{(sectors.get(cnpj) || []).map((item) => (
												<MenuItem
													key={item.id_setor}
													value={item.id_setor}
												>
													{item.setor}
												</MenuItem>
											))}
					
										</Select>
									</FormControl>
									<FormControl sx={{width: { xs: "100%", sm: "28vh"}}}>
										<InputLabel>
											Cargo
										</InputLabel>
										<Select
											label='Cargo'
											id="select-form"
											value={position}
											onChange={(e) => handleChangePosition(e.target.value)}
											size='medium'
											error={firstClick && !position}
											disabled={!sector}
										>
											{positions.get(sector)?.map((item) => (
												<MenuItem
													key={item.id_cargo}
													value={item.id_cargo}
												>
													{`${item.cargo} - ${item.funcao} - ${item.CBO}`}
												</MenuItem>
											))}
											<MenuItem
												key={'-1'}
												value={'-1'}
											>
												Outro cargo - Descrever
											</MenuItem>
										</Select>
									</FormControl>
								</div>
								{position === '-1'
									? <TextField
										label='Descreva o novo cargo'
										value={newPosition}
										fullWidth
										error={firstClick && !newPosition}
										onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPosition(e.target.value)}
									/>
									: <></>
								}
								<div className="form-line">
									<Tooltip title="Geralmente mesma descrição do cargo" placement="bottom">
										<TextField
											id="name-function"
											label="Função"
											value={functionName}
											fullWidth
											onChange={(e: ChangeEvent<HTMLInputElement>) => setFunction(e.target.value)}
										/>
									</Tooltip>
									<Tooltip title="Número na carteira de identidade do funcionário" placement="right">
										<TextField
											id="rg"
											label="RG"
											value={rg}
											type="number"
											fullWidth
											onChange={(e: ChangeEvent<HTMLInputElement>) => setRg(e.target.value)}
										/>
									</Tooltip>
								</div>
								<div className="form-line">
									<InputMask mask="999.99999.99-9"
										value={pis}
										onChange={(e: ChangeEvent<HTMLInputElement>) => setPis(e.target.value)}
									>
										{(inputProps: any) => (
											<TextField
												{...inputProps}
												id="pis"
												label="NIT (PIS)"
												value={pis}
												error={pis !== '' && pis.includes("_")}
												fullWidth
											/>
										)}
									</InputMask>
									<TextField
										id="ctps"
										label="CTPS"
										value={ctps}
										fullWidth
										type="number"
										onChange={(e: ChangeEvent<HTMLInputElement>) => setCtps(e.target.value)}
									/>
								</div>
								<div className="form-line">
									<TextField
										id="e-social"
										label="Matrícula e-social"
										value={esocial}
										fullWidth
										onChange={(e: ChangeEvent<HTMLInputElement>) => setEsocial(e.target.value)}
									/>
									<FormControl sx={{width: {xs: "100%", sm: "55vh"}}}>
										<InputLabel>
											Categoria
										</InputLabel>
										<Select
											label='Categoria'
											id="category"
											value={category}
											onChange={(e) => setCategory(e.target.value)}
											size='medium'
										>
											{getCategories().map((item) => (
												<MenuItem
													key={item.code}
													value={item.code}
												>
													{`${item.code} - ${item.name}`}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</div>
								<div className="form-line">
									<Button
										variant="contained"
										onClick={() => cleanForm()}
										fullWidth
										color="info"
										sx={{height: "5.5vh"}}
									>
										Limpar formulário
									</Button>
									<Button
										variant="contained"
										fullWidth
										color="success"
										sx={{height: "5.5vh"}}
										onClick={() => createEmployee()}
										disabled={loadingCreate}
									>
										{loadingCreate
											? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
											: "Cadastrar"
										}
									</Button>
								</div>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
									Os dados informados são tratados para fins admissionais e mantidos por tempo limitado, conforme a <PrivacyPolicyLink label="Política de Privacidade e LGPD" />.
								</Typography>
							</>
							: <></>
						}
					</div>
				</div>
				<Divider orientation="vertical" variant="fullWidth" flexItem sx={{borderColor: "rgba(255, 255, 255, 0.5)"}}/>
				<EmployeeList update={updates} account={user}/>
			</div>
		</div>
	);

	async function validateCpf() {
		try {
			if (user.permissionType === "REGION" && !user.permissionCnpjs?.includes(cnpj)) {
				showError("Você não tem permissão para acessar este CNPJ.");
				return;
			}
			setLoadingCpfValidation(true);
			const response = await service.getEmployeeBasicData(cpf, cnpj);
			if (response && response.data) {
				setName(response.data.nome);
				setBirthDate(dayjs(response.data.data_nascimento, "DD/MM/YYYY"));
				setSex(response.data.sexo);
				if (!!response.data.error) {
					showError(response.data.error);
				}
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingCpfValidation(false);
		}
	}

	async function createEmployee() {
		setFirstClick(true);
		if (notAllowedCreate) return;
		setLoadingCreate(true);
		try {
			const newEmp = {
				cnpj: cnpj,
				CPF: cpf,
				nome: name,
				sexo: sex,
				data_nascimento: birthDate.format('YYYY-MM-DD'),
				data_admissao: admissionDate.format('YYYY-MM-DD'),
				id_setor: sector,
				setor: sectorName,
				id_cargo: position,
				cargo: positionName,
				funcao: functionName,
				RG: rg,
				NIT: pis,
				CTPS: ctps,
				matricula: esocial,
				categoria: category,
				novo_cargo: newPosition
			} as EmployeeDTO;
			const response = await service.createEmployee(newEmp);
			setUpdates(prev => prev + 1);
			if (response?.status === 200 && !response.data) {
				showAlert(`Funcionário criado com sucesso`, "success");
				cleanForm();
			}
			else if (response && !!response.data) {
				showError(response.data);
			}
		} catch (error) {
			showError();
		} finally {
			setFirstClick(false);
			setLoadingCreate(false);
		}
	}
}

export default Employees;
